import { request } from './request';

/**
 * @typedef {Object} CitationData
 * @property {Array<{ref: number, uuids: string[]}>} citations
 */

/**
 * @typedef {Object} Message
 * @property {string} id - UUID v7 string
 * @property {'user'|'assistant'} role - Message role
 * @property {string} content - Message content
 * @property {CitationData} [citations] - Optional citation data
 * @property {string} createdAt - ISO 8601 timestamp
 */

/**
 * @typedef {Object} Conversation
 * @property {string} id - UUID v7 string
 * @property {string} title - Conversation title
 * @property {string} assistantName - Name of the AI assistant
 * @property {string} [model] - AI model identifier
 * @property {string} createdAt - ISO 8601 timestamp
 * @property {string} updatedAt - ISO 8601 timestamp
 * @property {string|null} deletedAt - Soft delete timestamp or null
 * @property {number} messageCount - Number of messages in conversation
 * @property {number} [rank] - Search relevance rank (search results only)
 */

/**
 * @typedef {Object} ConversationDetail
 * @property {string} id - UUID v7 string
 * @property {number} userId - Reference to user_account
 * @property {string} title - Conversation title
 * @property {string} assistantName - Name of the AI assistant
 * @property {string} [model] - AI model identifier
 * @property {string} createdAt - ISO 8601 timestamp
 * @property {string} updatedAt - ISO 8601 timestamp
 * @property {string|null} deletedAt - Soft delete timestamp or null
 * @property {Message[]} messages - Array of messages
 */

/**
 * @typedef {Object} Pagination
 * @property {number} page - Current page number
 * @property {number} limit - Items per page
 * @property {number} totalCount - Total number of items
 * @property {number} totalPages - Total number of pages
 */

/**
 * @typedef {Object} ConversationListResponse
 * @property {Conversation[]} conversations - Array of conversations
 * @property {Pagination} pagination - Pagination metadata
 */

/**
 * Get paginated list of conversations for the current user
 * @param {number} [page=1] - Page number
 * @param {number} [limit=20] - Items per page
 * @param {boolean} [includeDeleted=false] - Include soft-deleted conversations
 * @returns {Promise<ConversationListResponse>}
 */
export const getConversations = (page = 1, limit = 20, includeDeleted = false) => {
  return request(
    `/conversations?page=${page}&limit=${limit}&includeDeleted=${includeDeleted}`,
    'GET'
  );
};

/**
 * Search conversations by title and message content
 * @param {string} query - Search query string
 * @param {number} [page=1] - Page number
 * @param {number} [limit=20] - Items per page
 * @returns {Promise<ConversationListResponse>}
 */
export const searchConversations = (query, page = 1, limit = 20) => {
  const encodedQuery = encodeURIComponent(query);
  return request(`/conversations/search?q=${encodedQuery}&page=${page}&limit=${limit}`, 'GET');
};

/**
 * Create a new conversation
 * @param {string} title - Conversation title (max 256 chars)
 * @param {string} assistantName - Name of the AI assistant (max 128 chars)
 * @param {string|null} [model=null] - AI model identifier (max 64 chars, optional)
 * @returns {Promise<Conversation>}
 * @note Check response headers for X-Conversation-Limit-Remaining
 */
export const createConversation = (title, assistantName, model = null) => {
  const body = { title, assistantName };
  if (model) {
    body.model = model;
  }
  return request('/conversations', 'POST', body);
};

/**
 * Get detailed conversation with all messages
 * @param {string} id - Conversation UUID
 * @returns {Promise<ConversationDetail>}
 */
export const getConversation = (id) => {
  return request(`/conversations/${id}`, 'GET');
};

/**
 * Update conversation title
 * @param {string} id - Conversation UUID
 * @param {string} title - New title (max 256 chars)
 * @returns {Promise<Conversation>}
 */
export const updateConversationTitle = (id, title) => {
  return request(`/conversations/${id}`, 'PATCH', { title });
};

/**
 * Soft delete a conversation
 * @param {string} id - Conversation UUID
 * @returns {Promise<void>}
 */
export const deleteConversation = (id) => {
  return request(`/conversations/${id}`, 'DELETE');
};

/**
 * Add a message to an existing conversation
 * @param {string} conversationId - Conversation UUID
 * @param {'user'|'assistant'} role - Message role
 * @param {string} content - Message content (max 2048 chars for user)
 * @param {CitationData|null} [citations=null] - Optional citation data for assistant messages
 * @returns {Promise<Message>}
 */
export const addMessage = (conversationId, role, content, citations = null) => {
  const body = { role, content };
  if (citations) {
    body.citations = citations;
  }
  return request(`/conversations/${conversationId}/messages`, 'POST', body);
};
